import React from "react";
import { Helmet } from "react-helmet-async";
import ReactMarkdown from "react-markdown";

import DocPage from "../components/DocPage";
import { toMarkdown } from "../utils/doc";

const policySections = [
  {
    title: "Welcome to BLEEP Privacy Policy",
    content: [
      `Effective date: September 1, 2025`,
      `BLEEP is owned and operated by Anti Counterfeit Hub Africa Ltd, Plot 50 Windsor Crescent, Kololo, P.O. Box 6074, Kampala, Uganda.`,
      `This Privacy Policy explains what data we collect, how we use it, when we share it, and the choices you have.`,
      `Questions or requests: info@bleep.ug`,
    ],
  },
  {
    title: "Scope",
    content: [
      `This Policy applies to the BLEEP mobile apps, websites, APIs, and related services including reporting tools, brand and regulator portals, moderation, analytics dashboards, notifications, and support.`,
      `It covers users, reporters, brand and regulator accounts, visitors, and other people who interact with BLEEP.`,
    ],
  },
  {
    title: "Data we collect",
    content: [
      `Account and profile: username/display name, password, email, phone, country, language, avatar.`,
      `Reports and content: photos, videos, descriptions, product details, barcodes or IRIS codes, timestamps, comments, case notes.`,
      `Location: precise or approximate location you choose to share with a scan or report.`,
      `Device and usage: device identifiers, OS, app version, IP address, time zone, feature usage, crash logs, performance data, push tokens.`,
      `Permissions: camera and photo library access to capture or upload evidence; location for hotspot mapping.`,
      `Partner information: authenticity outcomes, enforcement actions, and program metadata from participating brands and regulators.`,
      `Cookies and SDKs: authentication, preferences, analytics, security, and abuse prevention.`,
    ],
  },
  {
    title: "How we use your data",
    content: [
      `Provide the service: create and manage accounts, enable reporting and verification, case workflows, search, and support.`,
      `Safety and abuse prevention: detect spam, fraud, misuse, and protect community integrity.`,
      `Verification and enforcement: help brands and regulators investigate and act on suspected counterfeits.`,
      `Communication: service messages, case updates, security alerts, and optional product news.`,
      `Improvement and research: analytics, debugging, feature testing, and aggregated insights about hotspots. We do not attempt to re-identify aggregated data.`,
      `Legal compliance: meet legal obligations and enforce our Terms and Community Guidelines.`,
    ],
  },
  {
    title: "AI processing and automated decisions",
    content: [
      `We use computer vision and other machine learning tools to assist detection, triage, prioritization, and hotspot mapping.`,
      `Automated outputs may be reviewed by people. Final enforcement actions can involve human review.`,
      `Model improvement: we may use your content to improve models. To opt out of model training without affecting features necessary to provide the service, email info@bleep.ug with your account details.`,
    ],
  },
  {
    title: "When we share your data",
    content: [
      `Brands and regulators: when you submit a report or scan to a program, we share necessary details so they can verify and respond. If you choose to report anonymously, we limit identity sharing as much as possible while enabling response.`,
      `Service providers: hosting, storage, analytics, security, messaging, and support vendors under confidentiality and security obligations.`,
      `Other users and the public: content posted to public areas may be visible to others; we may show aggregated hotspot data without personal identifiers.`,
      `Legal: to comply with laws or legal process, enforce our Terms, or protect rights, property, and safety.`,
      `Business transfers: your data may transfer in a merger, acquisition, or asset sale under this Policy.`,
      `We do not sell your personal data.`,
    ],
  },
  {
    title: "Legal bases for processing",
    content: [
      `Where required by law, we rely on:`,
      `Contract: to provide services you request.`,
      `Legitimate interests: safety, improvement, anti-abuse, and analytics that respect your rights.`,
      `Consent: location, camera, photo library, and marketing communications.`,
      `Legal obligation: compliance and record-keeping.`,
    ],
  },
  {
    title: "International transfers",
    content: [
      `We may process and store data in countries other than yours. Where required, we use safeguards such as contractual commitments. By using BLEEP, you understand your data may be transferred to countries that may not provide the same level of protection as your home country.`,
    ],
  },
  {
    title: "Retention",
    content: [
      `We keep data only as long as needed for the service and legitimate purposes:`,
      `Account data: while your account is active and for a reasonable period after deletion.`,
      `Reports and case files: up to 5 years to support investigations and legal obligations, unless law requires a different period.`,
      `Logs and analytics: typically up to 18 months.`,
      `Push tokens: until you disable notifications or uninstall the app.`,
      `We delete or anonymize data when it is no longer needed.`,
    ],
  },
  {
    title: "Your choices and rights",
    content: [
      `Depending on your location, you may have rights to access, correct, delete, restrict, or object to processing and to request portability.`,
      `Access and correction: in the app where available or by emailing info@bleep.ug.`,
      `Delete your account: from Settings or by contacting us.`,
      `Withdraw consent: disable location, camera, photos, and marketing in device or app settings.`,
      `Opt out of model training: email info@bleep.ug.`,
      `We may ask you to verify your identity before fulfilling a request.`,
    ],
  },
  {
    title: "Cookies and similar technologies",
    content: [
      `We use cookies, SDKs, and similar tools to keep you signed in, remember preferences, measure performance, and protect accounts.`,
      `You can control cookies in your browser and manage in-app tracking permissions in your device settings.`,
    ],
  },
  {
    title: "Children",
    content: [
      `BLEEP is for users at least 13 years old. Minors may use the service with a parent or legal guardian’s consent.`,
      `If you believe we collected personal data from a child without permission, contact us so we can take appropriate action.`,
    ],
  },
  {
    title: "Security",
    content: [
      `We use administrative, technical, and physical safeguards including encryption in transit and at rest, access controls, logging, and regular reviews.`,
      `No system is perfectly secure. We will notify you of data breaches as required by law.`,
    ],
  },
  {
    title: "Brand and regulator accounts",
    content: [
      `If you use BLEEP on behalf of an organization, we may collect role, work email, and organization details.`,
      `Your use may be subject to your organization’s policies.`,
    ],
  },
  {
    title: "Third-party links",
    content: [
      `BLEEP may link to third-party sites or services. Their privacy practices are governed by their own policies.`,
    ],
  },
  {
    title: "Changes to this Policy",
    content: [
      `We may update this Policy from time to time.`,
      `If changes are material, we will provide a prominent notice in the app or on our website.`,
      `Your continued use after the effective date of an updated Policy means you accept the changes.`,
    ],
  },
  {
    title: "Contact us",
    content: [
      `Data Controller: Anti Counterfeit Hub Africa Ltd`,
      `Address: Plot 50 Windsor Crescent, Kololo, P.O. Box 6074, Kampala, Uganda`,
      `Email: info@bleep.ug`,
      `You may have the right to lodge a complaint with your data protection authority.`,
    ],
  },
  {
    title: "Governing law",
    content: [
      `This Privacy Policy and any disputes related to it are governed by the laws of Uganda. The courts of Uganda have exclusive jurisdiction.`,
    ],
  },
  {
    title: "Effective date",
    content: [`This Privacy Policy is effective on September 1, 2025.`],
  },
];

const PrivacyPolicy = ({ domain }) => {
  const sections = policySections.map(({ title, content }) => ({
    title,
    body: (
      <div className="bl-doc__prose">
        <ReactMarkdown>{toMarkdown(content)}</ReactMarkdown>
      </div>
    ),
  }));

  return (
    <>
      <Helmet>
        <title>Privacy Policy | BLEEP</title>
        <meta
          name="description"
          content="What data BLEEP collects, how it is used, when it is shared, and the choices you have."
        />
      </Helmet>

      <DocPage
        title="Privacy Policy"
        lead="What we collect, how we use it, when we share it, and the choices you have."
        meta="Effective 1 September 2025"
        sections={sections}
        domain={domain}
      />
    </>
  );
};

export default PrivacyPolicy;
